// Renders seeded lesson text (ALL-CAPS section headers, "• " bullets, labeled scenario lines)
// as a designed lesson instead of a wall of preformatted text.

const HEADER_RE = /^[A-Z0-9 ,'"&()\/—–-]{4,}$/;

const SECTION_STYLES = [
  { match: /^(MISSION CONNECTION|CORE STANDARD)/i, kind: "mission" },
  { match: /^(PRACTICE SITUATION|A VISIT IN PRACTICE|SECOND SCENARIO|THIRD SCENARIO)/i, kind: "scenario" },
  { match: /(PITFALLS|NEVER LIST)/i, kind: "pitfalls" },
  { match: /^(THINK BEFORE|REFLECTION)/i, kind: "reflection" },
  { match: /^(NOTICE, STOP|STOP AND CALL|WHAT UNDERMINES|WHEN .+ GOES WRONG)/i, kind: "alert" },
];

const KIND_CLS = {
  mission: { wrap: "border-primary/30 bg-primary/5", head: "text-primary" },
  scenario: { wrap: "border-[#B7791F]/30 bg-[#B7791F]/5", head: "text-[#8a5d14]" },
  pitfalls: { wrap: "border-[#C0392B]/25 bg-[#C0392B]/5", head: "text-[#C0392B]" },
  reflection: { wrap: "border-stone-300 bg-accent/60", head: "text-stone-700" },
  alert: { wrap: "border-[#C0392B]/25 bg-white", head: "text-[#C0392B]" },
  plain: { wrap: "border-stone-200 bg-white", head: "text-stone-800" },
};

const LABEL_RE = /^(Situation|Best response|Why it matters|Why|What the record should show|Applied guidance|Silver Home Care response|Production direction):\s*/;

function parseLesson(reading) {
  const sections = [];
  let cur = null;
  const push = () => { if (cur && (cur.items.length || cur.intro.length)) sections.push(cur); };
  for (const raw of reading.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith("Electronic attestation:")) {
      push();
      sections.push({ kind: "attest", header: "", intro: [{ text: line.replace(/^Electronic attestation:\s*/, "") }], items: [] });
      cur = null;
      continue;
    }
    if (HEADER_RE.test(line) && /[A-Z]/.test(line)) {
      push();
      const style = SECTION_STYLES.find((s) => s.match.test(line));
      cur = { kind: style ? style.kind : "plain", header: line, intro: [], items: [] };
      continue;
    }
    if (!cur) cur = { kind: "plain", header: "", intro: [], items: [] };
    if (line.startsWith("• ")) cur.items.push({ bullet: true, text: line.slice(2) });
    else if (line.startsWith("Mission connection:") || line.startsWith("Core standard:")) {
      const [label, ...rest] = line.split(/:\s(.+)/);
      cur.intro.push({ label, text: rest[0] || "" });
      if (!cur.header && cur.kind === "plain") cur.kind = "mission";
    } else {
      const m = line.match(LABEL_RE);
      if (m) cur.items.push({ bullet: false, label: m[1], text: line.slice(m[0].length) });
      else cur.intro.push({ text: line });
    }
  }
  push();
  return sections.filter((s) => s.header || s.intro.length || s.items.length);
}

export function LessonBody({ reading }) {
  const sections = parseLesson(reading || "");
  return (
    <div className="space-y-4" data-testid="lesson-body">
      {sections.map((s, i) => {
        const cls = KIND_CLS[s.kind] || KIND_CLS.plain;
        if (s.kind === "attest") {
          return (
            <p key={i} className="rounded-lg border border-dashed border-stone-300 bg-accent/40 px-4 py-3 text-xs italic leading-relaxed text-stone-500" data-testid="lesson-attestation-note">
              You'll confirm this when you sign: {s.intro[0]?.text}
            </p>
          );
        }
        return (
          <section key={i} className={`rounded-xl border px-5 py-4 ${cls.wrap}`} data-testid={`lesson-section-${i}`}>
            {s.header && (
              <h3 className={`font-heading text-xs font-bold uppercase tracking-wider ${cls.head}`}>{s.header}</h3>
            )}
            {s.intro.map((p, j) => (
              <p key={j} className={`mt-2 text-sm leading-relaxed text-stone-700 ${s.kind === "mission" ? "font-medium" : ""}`}>
                {p.label && <span className="mr-1 font-semibold text-stone-900">{p.label}:</span>}
                {p.text}
              </p>
            ))}
            {s.items.length > 0 && (
              <ul className="mt-2 space-y-1.5">
                {s.items.map((it, j) => (
                  <li key={j} className={`flex gap-2 text-sm leading-relaxed text-stone-700 ${it.bullet ? "" : "mt-2"}`}>
                    {it.bullet && <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />}
                    <span>
                      {it.label && <span className="font-semibold text-stone-900">{it.label}: </span>}
                      {it.text}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
